import React from 'react';
import { Add as AddIcon, Public as GlobalIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { type RuleScope, type RuleType } from '../../types';

import { OrganizationSelector } from './OrganizationSelector';

interface AdminRulesHeaderProps {
  /** Currently selected organization ID, null = Global context */
  selectedOrgId: string | null;
  /** Currently selected organization name */
  selectedOrgName: string | null;
  /** Callback when organization changes */
  onOrgChange: (orgId: string | null, orgName: string | null) => void;
  /** Current search query */
  searchQuery: string;
  /** Callback when search query changes */
  onSearchChange: (query: string) => void;
  /** Current scope filter */
  scope: RuleScope;
  /** Callback when scope filter changes */
  onScopeChange: (scope: RuleScope) => void;
  /** Current rule type filter */
  ruleType: RuleType | 'all';
  /** Callback when rule type filter changes */
  onRuleTypeChange: (type: RuleType | 'all') => void;
  /** Callback when add rule button is clicked */
  onAddRule: () => void;
}

/**
 * AdminRulesHeader Component
 *
 * Header for admin rules management page with organization selector,
 * search, and filter controls.
 */
export const AdminRulesHeader: React.FC<AdminRulesHeaderProps> = ({
  selectedOrgId,
  selectedOrgName,
  onOrgChange,
  searchQuery,
  onSearchChange,
  scope,
  onScopeChange,
  ruleType,
  onRuleTypeChange,
  onAddRule,
}) => {
  const isGlobalContext = selectedOrgId === null;

  return (
    <Box sx={{ mb: 4 }}>
      {/* Title and Organization Selector Row */}
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 600, color: 'textColors.darkText', mb: 0.5 }}
          >
            Admin Rules Management
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" color="text.secondary">
              {isGlobalContext
                ? 'Managing global rules available to all organizations'
                : `Managing rules for ${selectedOrgName || selectedOrgId}`}
            </Typography>
            {isGlobalContext && (
              <Chip
                icon={<GlobalIcon sx={{ fontSize: 16 }} />}
                label="Global"
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <OrganizationSelector
            selectedOrgId={selectedOrgId}
            onOrgChange={onOrgChange}
            label="Organization Context"
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={onAddRule}>
            Add Rule
          </Button>
        </Box>
      </Box>

      {/* Search and Filters Row */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: "center" }}>
        <TextField
          placeholder="Search rules..."
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 300 }}
         slotProps={{ input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          } }}/>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="admin-rule-scope-label">Scope</InputLabel>
          <Select
            labelId="admin-rule-scope-label"
            value={scope}
            label="Scope"
            onChange={(e: SelectChangeEvent) => onScopeChange(e.target.value as RuleScope)}
          >
            <MenuItem value="both">All Scopes</MenuItem>
            <MenuItem value="global">Global Only</MenuItem>
            <MenuItem value="org">Organization Only</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="admin-rule-type-label">Rule Type</InputLabel>
          <Select
            labelId="admin-rule-type-label"
            value={ruleType}
            label="Rule Type"
            onChange={(e: SelectChangeEvent) =>
              onRuleTypeChange(e.target.value as RuleType | 'all')
            }
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="compliance">Compliance</MenuItem>
            <MenuItem value="learning">Learning</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Box>
  );
};

export default AdminRulesHeader;


